import {parseCookies} from 'nookies'
import {useMutation} from '@apollo/client'
import {CartItemUpdateInput, GetCartQuery} from '_app/types/api'
import {useUpdateCartItemsMutation} from '_app/graphql/mutations/cart/UpdateCartItemsDocument'
import toast from '_app/utils/toast'
import {REMOVE_ITEM_FROM_CART} from '_app/graphql/mutations/cart/RemoveItemFromCart'
import {CartItem} from '../types'
import {cart} from '../../../_app/cache'
import RequestHelper from '../../../helpers/RequestHelper/RequestHelper'
import {useRouter} from 'next/router'
import {getCurrencyCode, getDimension4, GTM_EVENTS} from '../../../_app/utils/dataLayer'
import ProductHelper from '../../../helpers/ProductHelper/ProductHelper'

export const useCartItems = (data: GetCartQuery | undefined, setData: any = null) => {
  const { cartId } = parseCookies()
  const { locale } = useRouter()
  const [updateCartItemsMutation] = useUpdateCartItemsMutation()
  const [removeItemFromCart, { loading }] = useMutation(REMOVE_ITEM_FROM_CART)
  const deleteProductHandler = async (item: any) => {
    return await removeItemFromCart({
      variables: {
        cartId,
        cartItemId: item?.id,
      },
      onCompleted: ((res: any) => {
        if (res) {
          RequestHelper.onComplete(res, 'removeItemFromCart', locale)
          cart(res?.removeItemFromCart)
          GTM_EVENTS.removeFromCart({
            type:
                item?.__typename === 'SimpleProduct'
                    ? 'standard_product'
                    : item?.__typename === 'BundleProduct'
                        ? ProductHelper.isPersonalizedProduct(item?.product)
                            ? 'personalized_product'
                            : 'configured_products'
                        : '',
            origin: 'product page',
            ecommerce: {
              currencyCode: getCurrencyCode(item),
              remove: {
                products: [
                  {
                    id: item?.uid,
                    name: item?.product?.name,
                    quantity: item?.quantity,
                    price:
                        item?.produt?.price_range?.minimum_price?.final_price
                            ?.value as number,
                    dimension4: getDimension4(item?.categories),
                    category1: item?.produt?.main_category?.[0]?.name,
                    brand: 'Lilou',
                    dimension8: ProductHelper.isPersonalizedProduct(
                        item?.product
                    )
                        ? 'Personalizowany'
                        : 'Standard',
                  },
                ],
              },
            },
          })
          if (setData) {
            setData(res?.removeItemFromCart)
          }
        }
      })
    }).catch((error) => RequestHelper.internalServerError(error))
  }

  const quantityChangeHandler = async (itemId: string, quantity: number) => {
    const updatedItem: any = data?.cart?.items?.find(item => item?.id === itemId)
    const cartItems: CartItemUpdateInput[] = (data?.cart?.items || []).map(
      item => ({
        cart_item_uid: item?.uid,
        quantity: item?.id === itemId ? quantity : item?.quantity,
      })
    )
    try {
      return await updateCartItemsMutation({
        variables: {
          cart_id: cartId,
          cart_items: cartItems,
        },
        onCompleted: (res: any) => {
          if (res) {
            cart(res?.updateCartItems)
            if (updatedItem?.quantity < quantity) {
              GTM_EVENTS.addToCart({
                type:
                    updatedItem?.__typename === 'SimpleProduct'
                        ? 'standard_product'
                        : updatedItem?.__typename === 'BundleProduct'
                            ? ProductHelper.isPersonalizedProduct(updatedItem?.product)
                                ? 'personalized_product'
                                : 'configured_products'
                            : '',
                origin: 'product page',
                ecommerce: {
                  currencyCode: getCurrencyCode(updatedItem),
                  add: {
                    products: [
                      {
                        id: updatedItem?.uid,
                        name: updatedItem?.product?.name,
                        quantity: 1,
                        price:
                            updatedItem?.produt?.price_range?.minimum_price?.final_price
                                ?.value as number,
                        dimension4: getDimension4(updatedItem?.categories),
                        category1: updatedItem?.produt?.main_category?.[0]?.name,
                        brand: 'Lilou',
                        dimension8: ProductHelper.isPersonalizedProduct(
                            updatedItem?.product
                        )
                            ? 'Personalizowany'
                            : 'Standard',
                      },
                    ],
                  },
                },
              })
            } else {
              GTM_EVENTS.removeFromCart({
                type:
                    updatedItem?.__typename === 'SimpleProduct'
                        ? 'standard_product'
                        : updatedItem?.__typename === 'BundleProduct'
                            ? ProductHelper.isPersonalizedProduct(updatedItem?.product)
                                ? 'personalized_product'
                                : 'configured_products'
                            : '',
                origin: 'product page',
                ecommerce: {
                  currencyCode: getCurrencyCode(updatedItem),
                  remove: {
                    products: [
                      {
                        id: updatedItem?.uid,
                        name: updatedItem?.product?.name,
                        quantity: 1,
                        price:
                            updatedItem?.produt?.price_range?.minimum_price?.final_price
                                ?.value as number,
                        dimension4: getDimension4(updatedItem?.categories),
                        category1: updatedItem?.produt?.main_category?.[0]?.name,
                        brand: 'Lilou',
                        dimension8: ProductHelper.isPersonalizedProduct(
                            updatedItem?.product
                        )
                            ? 'Personalizowany'
                            : 'Standard',
                      },
                    ],
                  },
                },
              })
            }
            if (setData) {
              setData(res?.updateCartItems)
            }
          }
        }
      })
    } catch (error) {
      toast(error)
    }
  }

  return { quantityChangeHandler, deleteProductHandler, loading }
}
